/* eslint-disable import/max-dependencies */

import React, { useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import { LineDetails } from '../../hooks/deal-details';
import { useTranslate } from '@im/react-localization';
import { useSearchParams } from 'react-router-dom';

import {
  commaSeparatedValue,
  mergedUniqueList,
  Product,
  useAddVpnUpdate,
  VpnAddQueryRequest,
} from '../../hooks/vpn-list';
import { DataGrid, LoadingOverlay } from 'src/components/DataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { StyledText } from 'src/components/StyledTypography';
import Delete from '@mui/icons-material/DeleteOutline';
import { VpnErrorMessage, VpnButton } from './VpnErrorMessage';

interface AddVpnPageProps {
  handleClose?: () => void | undefined;
  lineDetails: LineDetails;
  lineDetailsvpn: boolean;
  onAddvpnlist: (item: Product[]) => void;
  productMemberType: string;
  reprocessMsgprops: () => void;
  selectedMode?: string;
  vpnlistRows: Product[];
}
export default function AddVpnPage(props: Readonly<AddVpnPageProps>) {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const vendorProgramId = searchParams.get('vendorProgramId');
  const [inputValue, setInputValue] = useState<string>('');
  const [rows, setRows] = useState<Product[]>([]);
  const [mode, setMode] = useState<string>(props.selectedMode ?? 'No');
  const [memberType, setMemberType] = useState<string>(
    props.productMemberType ?? 'VPN'
  );

  const handleModeChange = (event: SelectChangeEvent) => {
    setMode(event.target.value);
  };

  const handeldelteRow = (id: number) => {
    setRows((prevRows) => prevRows.filter((row) => row.Id !== id));
  };
  const columns: GridColDef<typeof rows[number]>[] = [
    {
      align: 'left',
      cellClassName: 'cellvender',
      field: 'productMember',
      flex: 2,
      headerName: translate('VendorDeals.DiscountLineVpnName'),
      renderCell: (item) => {
        const value = <StyledText noWrap>{item.value}</StyledText>;
        return <Tooltip title={value}>{value}</Tooltip>;
      },
      sortable: false,
    },
    {
      align: 'right',
      cellClassName: 'cellvender',
      field: '',
      flex: 1,
      headerName: '',
      renderCell: (item) => (
        <IconButton
          data-testid="gds-vp-vpn-list-delterow"
          onClick={() => handeldelteRow(item.row.Id)}
        >
          <Delete />
        </IconButton>
      ),
      sortable: false,
    },
  ];
  const handleAddVpns = () => {
    const newItems = commaSeparatedValue(rows, inputValue);
    setRows((prevItem) => [...prevItem, ...newItems]);
    setInputValue('');
  };
  const requestBodyAddvpn: VpnAddQueryRequest = {
    dealId: String(props.lineDetails.deal_number),
    discountDetailId: props.lineDetails.discount_detail_id,
    isProductMemberExclude: mode,
    productMemberType: props.productMemberType,
    productMembers: mergedUniqueList(rows, props.vpnlistRows).map(
      (item) => item.productMember
    ),
    sbo2ProductSetId: props.lineDetails.sbo2_product_set_Id,
    vendorProgramId: Number(vendorProgramId),
  };
  const VpnAddDetail = useAddVpnUpdate(requestBodyAddvpn);
  const [reprocessMsg, setReprocessMsg] = useState<string | undefined>();
  const handleAddSKUlist = () => {
    VpnAddDetail.mutateAsync()
      .then((response) => setReprocessMsg('Success'))
      .catch((error) => setReprocessMsg(error));
    props.onAddvpnlist(rows);
  };
  return (
    <Drawer
      anchor={'right'}
      data-testid="gds-vp-add-vpn-drawer"
      onClose={() => {
        props.reprocessMsgprops();
        props.handleClose?.();
      }}
      open={Boolean(props.lineDetailsvpn)}
      sx={{
        '.MuiDrawer-paperAnchorRight': {
          height: `calc(100vh - 50px)`,
          top: 50,
          width: '30%',
        },
      }}
    >
      <Stack
        data-testid="gds-vp-order-history-drawer-header"
        gap={2}
        paddingTop={2}
        paddingX={2}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography
            data-testid="gds-vp-VPN-Detail-table heading"
            fontSize={16}
            lineHeight={`22px`}
            sx={{ alignContent: 'center', display: 'grid' }}
            variant="smalltextsemibold"
          >
            {translate('VendorProgram.VendorVpn', {
              id: props.lineDetails.vendor_number,
            })}
          </Typography>
          <IconButton
            data-testid="gds-vp-vpnadd-icon-btn"
            onClick={() => {
              props.reprocessMsgprops();
              props.handleClose?.();
            }}
            size="small"
            sx={{ alignSelf: 'baseline' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <Stack direction={'row'} spacing={2}>
          <Select
            disabled={props.vpnlistRows.length > 0}
            onChange={handleModeChange}
            size="small"
            sx={{
              '&.multiSelect-icon': {
                border: '1px',
                color: 'blue',
              },
              color: '#0073E5',
              height: '30px',
              lineHeight: '30px',
            }}
            value={mode}
            variant={'outlined'}
          >
            <MenuItem value="No">{translate('VendorProgram.Include')}</MenuItem>
            <MenuItem value="Yes">
              {translate('VendorProgram.Exclude')}
            </MenuItem>
          </Select>
          <Select
            disabled={props.vpnlistRows.length > 0}
            onChange={(e) => setMemberType(e.target.value)}
            size="small"
            sx={{
              color: '#0073E5',
              height: '30px',
              lineHeight: '30px',
            }}
            value={memberType}
            variant="outlined"
          >
            <MenuItem value="VPN">
              {translate('VendorProgram.VPNProductMemberType')}
            </MenuItem>
            <MenuItem value="SKU">
              {translate('VendorProgram.SKUProductMemberType')}
            </MenuItem>
          </Select>
        </Stack>
      </Stack>
      {reprocessMsg === 'Success' || reprocessMsg === 'Yes' ? (
        <VpnErrorMessage
          MessageProp={reprocessMsg}
          ModeProp={mode}
          RowsProp={rows}
          is_product_member_exclude={
            props.lineDetails.is_product_member_exclude
          }
        />
      ) : (
        <Stack>
          <Stack flexDirection={'row'} justifyContent={'center'}>
            <Typography gutterBottom variant="h6">
              {translate('VendorProgram.AddProduct')}
            </Typography>
          </Stack>
          <Stack>
            <Typography pl={2} pr={2} pt={2}>
              {translate('VendorProgram.EnterVpn')}
            </Typography>
          </Stack>
          <Stack
            direction={'row'}
            flex={1}
            justifyContent={'center'}
            p={2}
            spacing={2}
          >
            <TextField
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={translate('VendorProgram.EnterVpnPlaceholder')}
              sx={{ marginRight: '10px', width: '100%' }}
              type="text"
              value={inputValue}
            ></TextField>
            <Button
              data-testid="gds-vp-vpnadd-add-btn"
              disabled={inputValue.length === 0}
              onClick={handleAddVpns}
              startIcon={<AddIcon />}
              sx={{ paddingLeft: '21px', paddingRight: '29px' }}
              variant="newContained"
            >
              {' '}
              {translate('VendorProgram.AddProduct')}
            </Button>
          </Stack>
          {rows.length > 0 && (
            <Box overflow="auto" p={2}>
              <Stack>
                <Typography gutterBottom variant="subtitle1">
                  {' '}
                  {rows.length} {translate('VendorProgram.Products')}
                </Typography>
              </Stack>
              <DataGrid
                autoHeight
                columnBuffer={columns.length}
                columns={columns}
                components={{
                  LoadingOverlay: LoadingOverlay,
                }}
                data-testid="gds-vp-AddVpn-list-dataGrid"
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                disableSelectionOnClick
                getRowHeight={() => 'auto'}
                getRowId={(id) => id.Id}
                hideFooterPagination={true}
                rows={rows as any}
                sx={{
                  '.MuiDataGrid-columnHeader--moving': {
                    backgroundColor: 'transparent',
                  },
                  '.MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'normal',
                  },
                  '.MuiDataGrid-row': {
                    minHeight: '52px !important',
                  },
                }}
              />
            </Box>
          )}
          <VpnButton
            IsRemove={false}
            onhandleAddSKUlist={handleAddSKUlist}
            onhandleClose={props.handleClose}
            onreprocessMsgprops={props.reprocessMsgprops}
            rows={rows}
          />
        </Stack>
      )}
    </Drawer>
  );
}
